import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import useBackdropClose from "../../hooks/useBackdropClose";
import { API_URL } from "../../config/constants";
import ActivityTaxonomy from "../activityTaxonomy";
import CatalogItem from "../catalogItem";
import { formatAgeRange } from "../catalogItem/format";
import {
  ContextPickerModal, MaterialsPickerModal, PosturesPickerModal,
} from "../catalogPickerModal";
import activityTypeIcons from "../../assets/activity_types/icons";
import activityCardImages from "../../assets/activity_cards/images";
import {
  Overlay, Modal, Header, TypeIcon, Title, CloseButton,
  PrescribeRow, PrescribeBox, HiddenCheckbox, Image, MetaBlock, MetaLine,
  SectionLabel, Text, TabsBar, Tab, TabPanel, Status,
  ItemRow, RemoveButton, AddButton, Footer, FooterRight, OutlineButton, PrimaryButton, SaveError,
} from "./styles";

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);
const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);
const ExternalIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <path d="M15 3h6v6M10 14L21 3" />
  </svg>
);
const PlusSquareIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M12 8v8M8 12h8" />
  </svg>
);
const MinusSquareIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M8 12h8" />
  </svg>
);
const PencilIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

// "Educational" no es una pestaña: es un enlace.
const TABS = ["explanation", "context", "materials", "posture"];
const LISTS = ["contexts", "materials", "postures"];

const pickLists = (source) => ({
  contexts: source.contexts || [],
  materials: source.materials || [],
  postures: source.postures || [],
});

// Dos juegos de listas son iguales si tienen los mismos ids en el mismo orden.
const sameLists = (a, b) => LISTS.every((key) =>
  a[key].length === b[key].length && a[key].every((item, i) => item.id === b[key][i].id));

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

/**
 * Ficha de una actividad. La ven igual paciente y terapeuta. El terapeuta,
 * desde el plan de un paciente (patientId), además puede prescribirla y
 * personalizar para ese paciente el context, los materials y las postures.
 *
 * `activity` es la fila de la rejilla: con ella se pinta la cabecera al
 * instante mientras llega la ficha completa.
 *
 * La personalización se edita sobre un borrador (draft) y solo se guarda con
 * "Save changes". La prescripción, en cambio, es inmediata.
 */
const ActivityDetailModal = ({
  activity, onClose, canPrescribe = false, prescribed = false, onTogglePrescribed, patientId,
}) => {
  const { t, i18n } = useTranslation();
  const backdrop = useBackdropClose(onClose);
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState("loading");
  const [tab, setTab] = useState("explanation");
  const [draft, setDraft] = useState(null);
  const [picker, setPicker] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [catalogStatus, setCatalogStatus] = useState("idle");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const canEdit = canPrescribe && !!patientId;

  useEffect(() => {
    // Si se cierra o cambia de idioma antes de que responda, la respuesta vieja
    // no debe pisar a la nueva.
    let cancelled = false;
    setStatus("loading");
    const patientParam = patientId ? `&patient_id=${patientId}` : "";
    fetch(`${API_URL}/activities/${activity.id}?lang=${i18n.language}${patientParam}`, {
      headers: authHeaders(),
    })
      .then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setDetail(data);
        setDraft(pickLists(data));
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => { cancelled = true; };
  }, [activity.id, i18n.language, patientId]);

  // El catálogo solo hace falta para los selectores: se pide al abrir el
  // primero y se reutiliza mientras no cambie el idioma.
  useEffect(() => {
    if (!picker || catalog?.lang === i18n.language) return;
    let cancelled = false;
    setCatalogStatus("loading");
    fetch(`${API_URL}/activities/catalog?lang=${i18n.language}`, { headers: authHeaders() })
      .then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setCatalog({ ...data, lang: i18n.language });
        setCatalogStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setCatalogStatus("error");
      });
    return () => { cancelled = true; };
  }, [picker, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = detail || activity;
  const image = shown.image_url || activityCardImages[shown.activity_type_id];
  const educationalUrl = detail?.educational_url;
  // El servidor solo manda la edad al terapeuta.
  const age = detail ? formatAgeRange(t, detail.age_min_months, detail.age_max_months) : "";

  const saved = detail ? pickLists(detail) : null;
  const defaults = detail?.defaults ? pickLists(detail.defaults) : null;
  const dirty = !!(draft && saved && !sameLists(draft, saved));
  const atDefaults = !!(draft && defaults && sameLists(draft, defaults));

  const updateDraft = (key, items) => {
    setDraft((prev) => ({ ...prev, [key]: items }));
    setSaveError(false);
  };
  const removeItem = (key, id) => updateDraft(key, draft[key].filter((item) => item.id !== id));

  const resetToDefaults = () => {
    setDraft(defaults);
    setSaveError(false);
  };

  // Si el resultado coincide con el catálogo se borra la personalización en vez
  // de guardar una copia: así el paciente recibe los cambios futuros del
  // catálogo en esa actividad.
  const save = async () => {
    setSaving(true);
    setSaveError(false);
    const url = `${API_URL}/activities/${activity.id}/patients/${patientId}/customization`;
    try {
      const res = atDefaults
        ? await fetch(url, { method: "DELETE", headers: authHeaders() })
        : await fetch(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({
            context_id: draft.contexts[0]?.id || null,
            material_ids: draft.materials.map((item) => item.id),
            posture_ids: draft.postures.map((item) => item.id),
          }),
        });
      if (!res.ok) throw new Error(res.status);
      onClose();
    } catch (err) {
      console.error("save customization error:", err);
      setSaveError(true);
      setSaving(false);
    }
  };

  const renderItems = (key, kind, removable) => draft[key].map((item) => (
    <ItemRow key={item.id}>
      <CatalogItem item={item} kind={kind} />
      {canEdit && removable && (
        <RemoveButton
          type="button"
          onClick={() => removeItem(key, item.id)}
          aria-label={`${t("activityCard.remove")}: ${item.label}`}
        >
          <MinusSquareIcon />
        </RemoveButton>
      )}
    </ItemRow>
  ));

  // Sin elementos, el terapeuta solo ve el botón de añadir; el paciente, un
  // aviso. Si la pestaña tiene texto general ("Volto caregiver."), ese texto ya
  // dice qué hace falta y el aviso sobra.
  const emptyNotice = (key, messageKey, description) =>
    !canEdit && draft[key].length === 0 && !description && <Text $muted>{t(messageKey)}</Text>;

  const renderPanel = () => {
    if (tab === "explanation") {
      return (
        <>
          {shown.specific_goal && (
            <div>
              <SectionLabel>{t("activityPlan.specificGoal")}</SectionLabel>
              <Text>{shown.specific_goal}</Text>
            </div>
          )}
          {detail.premise && (
            <div>
              <SectionLabel>{t("activityCard.premises")}</SectionLabel>
              <Text>{detail.premise}</Text>
            </div>
          )}
          <div>
            <SectionLabel>{t("activityCard.explanation")}</SectionLabel>
            <Text>{detail.explanation || "—"}</Text>
          </div>
        </>
      );
    }

    if (tab === "context") {
      return (
        <>
          <SectionLabel>{t("activityCard.context")}</SectionLabel>
          {emptyNotice("contexts", "activityCard.noContext")}
          {/* Solo hay un context: se cambia, no se quita. */}
          {renderItems("contexts", "contexts", false)}
        </>
      );
    }

    if (tab === "materials") {
      return (
        <>
          <SectionLabel>{t("activityCard.materials")}</SectionLabel>
          {detail.materials_description && <Text>{detail.materials_description}</Text>}
          {emptyNotice("materials", "activityCard.noMaterials", detail.materials_description)}
          {renderItems("materials", "materials", true)}
        </>
      );
    }

    return (
      <>
        <SectionLabel>{t("activityCard.posture")}</SectionLabel>
        {/* El servidor solo lo manda al terapeuta. */}
        {detail.postures_description && <Text>{detail.postures_description}</Text>}
        {emptyNotice("postures", "activityCard.noPostures")}
        {renderItems("postures", "postures", true)}
      </>
    );
  };

  // Botón de la pestaña. Va fuera de la zona con scroll para que siempre se vea.
  const renderAction = () => {
    if (!canEdit || tab === "explanation") return null;
    if (tab === "context") {
      return (
        <AddButton type="button" onClick={() => setPicker("context")}>
          <PencilIcon />
          {t("activityCard.editContext")}
        </AddButton>
      );
    }
    const isMaterials = tab === "materials";
    return (
      <AddButton type="button" onClick={() => setPicker(isMaterials ? "materials" : "postures")}>
        <PlusSquareIcon />
        {t(isMaterials ? "activityCard.addMaterials" : "activityCard.addPostures")}
      </AddButton>
    );
  };

  const closePicker = () => setPicker(null);
  const pickerProps = { catalog, status: catalogStatus === "idle" ? "loading" : catalogStatus, onCancel: closePicker };

  // Portal a <body>: desde la rejilla (dentro de un layout con z-index) el
  // overlay quedaria atrapado en su contexto de apilamiento, bajo la top bar.
  return createPortal(
    <>
      <Overlay {...backdrop}>
        <Modal
          role="dialog"
          aria-modal="true"
          aria-labelledby="activity-card-title"
          onClick={(e) => e.stopPropagation()}
        >
          <Header>
            <TypeIcon src={activityTypeIcons[shown.activity_type_id]} alt="" />
            <Title id="activity-card-title">{shown.name}</Title>
            <CloseButton type="button" onClick={onClose} aria-label={t("activityPlan.close")}>
              <CloseIcon />
            </CloseButton>
          </Header>

          <ActivityTaxonomy activity={shown} />

          {canPrescribe && (
            <PrescribeRow>
              <HiddenCheckbox checked={prescribed} onChange={onTogglePrescribed} />
              <PrescribeBox $checked={prescribed} aria-hidden="true">
                {prescribed && <CheckIcon />}
              </PrescribeBox>
              {t("activityCard.addToPlan")}
            </PrescribeRow>
          )}

          {image && <Image src={image} alt="" />}

          {/* Solo terapeuta (el servidor no se los manda al paciente), y solo
              si hay dato. */}
          {(age || detail?.clinical_notes) && (
            <MetaBlock>
              {age && (
                <MetaLine>
                  <strong>{t("activityCard.suggestedAge")}:</strong> {age}
                </MetaLine>
              )}
              {detail?.clinical_notes && (
                <MetaLine>
                  <strong>{t("activityCard.clinicalNotes")}:</strong> {detail.clinical_notes}
                </MetaLine>
              )}
            </MetaBlock>
          )}

          <TabsBar role="tablist">
            {TABS.map((key) => (
              <Tab
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                $active={tab === key}
                onClick={() => setTab(key)}
              >
                {t(`activityCard.${key}`)}
              </Tab>
            ))}
            {/* Sin URL todavía: se ve pero no hace nada. Con URL abre la web aparte. */}
            {educationalUrl ? (
              <Tab as="a" href={educationalUrl} target="_blank" rel="noreferrer">
                <ExternalIcon />
                {t("activityCard.educational")}
              </Tab>
            ) : (
              <Tab type="button" disabled>
                <ExternalIcon />
                {t("activityCard.educational")}
              </Tab>
            )}
          </TabsBar>

          {status === "loading" && <Status>{t("common.loading")}</Status>}
          {status === "error" && <Status>{t("activityCard.loadError")}</Status>}
          {/* La ficha tiene alto máximo y lo que scrollea es solo el contenido de
              la pestaña. key={tab}: al cambiar de pestaña vuelve arriba. */}
          {status === "ready" && (
            <>
              <TabPanel key={tab} role="tabpanel" tabIndex={0}>{renderPanel()}</TabPanel>
              {renderAction()}
            </>
          )}

          {canEdit && (
            <>
              {saveError && <SaveError role="alert">{t("activityCard.saveError")}</SaveError>}
              <Footer>
                <OutlineButton
                  type="button"
                  onClick={resetToDefaults}
                  disabled={status !== "ready" || atDefaults || saving}
                >
                  {t("activityCard.resetDefaults")}
                </OutlineButton>
                <FooterRight>
                  <OutlineButton type="button" onClick={onClose}>
                    {t("common.cancel")}
                  </OutlineButton>
                  <PrimaryButton type="button" onClick={save} disabled={!dirty || saving}>
                    {t("activityCard.saveChanges")}
                  </PrimaryButton>
                </FooterRight>
              </Footer>
            </>
          )}
        </Modal>
      </Overlay>

      {/* Fuera del Overlay: un clic en el selector no debe llegar al fondo de la ficha. */}
      {picker === "context" && (
        <ContextPickerModal
          {...pickerProps}
          selectedId={draft.contexts[0]?.id}
          onConfirm={(item) => {
            if (item) updateDraft("contexts", [item]);
            closePicker();
          }}
        />
      )}
      {picker === "materials" && (
        <MaterialsPickerModal
          {...pickerProps}
          selectedIds={draft.materials.map((item) => item.id)}
          onConfirm={(items) => {
            updateDraft("materials", items);
            closePicker();
          }}
        />
      )}
      {picker === "postures" && (
        <PosturesPickerModal
          {...pickerProps}
          selectedIds={draft.postures.map((item) => item.id)}
          onConfirm={(items) => {
            updateDraft("postures", items);
            closePicker();
          }}
        />
      )}
    </>,
    document.body
  );
};

export default ActivityDetailModal;
